/**
 * JWT token utilities
 */

interface JWTPayload {
  exp?: number; // Expiration time (seconds since epoch)
  iat?: number; // Issued at (seconds since epoch)
  sub?: string; // Subject (user ID)
  [key: string]: any;
}

/**
 * Decode JWT token and extract payload
 * Note: This does NOT validate the signature - validation happens on the backend
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];

    // Replace URL-safe base64 chars and add padding if needed
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');

    // Decode base64 and parse JSON
    const jsonPayload = atob(paddedBase64);
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Get expiration timestamp from JWT token (in milliseconds)
 * Returns null if token is invalid or has no expiration
 */
export function getJWTExpiration(token: string): number | null {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return null;
  }

  // Convert from seconds to milliseconds
  return payload.exp * 1000;
}

/**
 * Check if JWT token is expired
 */
export function isJWTExpired(token: string): boolean {
  const expirationMs = getJWTExpiration(token);
  if (!expirationMs) {
    // If we can't determine expiration, assume it's expired for safety
    return true;
  }

  return Date.now() >= expirationMs;
}
